import { FlagsContextModel } from 'config/useFlagsContext'
import { AcessoCbo, TipoAtendimentoProfissional } from 'graphql/types.generated'
import Permissions from 'types/Permissions'
import { TipoAtendimentoFooter } from 'view/atendimentos/model-atendimento'
import { getTiposEstabelecimentoCuidadoCompartilhado } from 'view/cuidados-compartilhados/util-cuidadoCompartilhado'

import {
  grupoCboAtestado,
  grupoCboCuidadoCompartilhado,
  grupoCboEncaminhamentoExterno,
  grupoCboOrientacoes,
  grupoCboPrescricao,
  grupoCboSolicitacaoExame,
} from '../acessos'
import { getIsVinculacaoCuidadoAtiva } from '../cuidado-compartilhado/cuidado-compartilhado-form/utils-cuidadoCompartilhado'
import { PlanoTabConfig, PlanoTabsEnum, SOLICITACAO_EXAMES_PROCEDIMENTOS_TAB_NAME } from './model-planoTabs'

export const getPlanoTabsConfig = (
  tipoAtendimentoProfissional: TipoAtendimentoFooter,
  isObservacaoAndAuxiliar: boolean,
  isRetificacao: boolean,
  isFolhaRosto: boolean,
  flags: FlagsContextModel,
  hasCboAuth: (acessos: AcessoCbo[]) => boolean
): { tabs: PlanoTabConfig[]; initialTab: PlanoTabsEnum } => {
  const { PILOTO_VINCULACAO_CUIDADO_ENABLED } = flags

  const isAtendimentoProcedimentos =
    tipoAtendimentoProfissional === TipoAtendimentoProfissional.ATENDIMENTO_PROCEDIMENTOS
  const isAtendimentoOdonto = tipoAtendimentoProfissional === TipoAtendimentoProfissional.ATENDIMENTO_ODONTOLOGICO
  const isAtendimentoIndividual = tipoAtendimentoProfissional === TipoAtendimentoProfissional.CONSULTA

  const isRetificacaoAtendimentoIndividual = isRetificacao && isAtendimentoIndividual
  const isRetificacaoAtendimentoProcedimentos = isRetificacao && isAtendimentoProcedimentos

  const isVinculacaoCuidadoAtiva = getIsVinculacaoCuidadoAtiva(flags, isAtendimentoOdonto)

  const showAtestado = !isAtendimentoProcedimentos && hasCboAuth(grupoCboAtestado.visualizar)
  const showDeclaracaoComparecimento = !showAtestado && !isFolhaRosto && !isRetificacao
  const showEncaminhamento =
    !isObservacaoAndAuxiliar &&
    !isRetificacaoAtendimentoIndividual &&
    !isAtendimentoProcedimentos &&
    !isVinculacaoCuidadoAtiva

  const showCuidadoCompartilhado = !isObservacaoAndAuxiliar && !isAtendimentoProcedimentos

  const labelCuidadoCompartilhado = isVinculacaoCuidadoAtiva
    ? 'Encaminhamento/Compartilhamento de cuidado'
    : 'Compartilhamento de cuidado'

  const tabs: PlanoTabConfig[] = [
    {
      enum: PlanoTabsEnum.PRESCRICAO_MEDICAMENTOS,
      label: 'Prescrição de medicamentos',
      testId: 'TabMedicamento',
      permissions: {
        permission: grupoCboPrescricao.visualizar,
      },
      showItem: !isRetificacaoAtendimentoProcedimentos,
    },
    {
      enum: PlanoTabsEnum.EXAMES,
      label: SOLICITACAO_EXAMES_PROCEDIMENTOS_TAB_NAME,
      testId: 'TabExame',
      permissions: {
        permission: grupoCboSolicitacaoExame.visualizar,
      },
      showItem: !isRetificacaoAtendimentoProcedimentos,
    },
    {
      enum: PlanoTabsEnum.ATESTADO,
      label: 'Atestados',
      testId: 'TabAtestado',
      permissions: {
        permission: grupoCboAtestado.visualizar,
      },
      showItem: showAtestado,
    },
    {
      enum: PlanoTabsEnum.ORIENTACOES,
      label: 'Orientações',
      testId: 'TabOrientacao',
      permissions: {
        permission: grupoCboOrientacoes.visualizar,
      },
      showItem: true,
    },
    {
      enum: PlanoTabsEnum.ENCAMINHAMENTO_EXTERNO,
      label: 'Encaminhamentos',
      testId: 'TabEncaminhamento',
      permissions: {
        permission: grupoCboEncaminhamentoExterno,
      },
      showItem: showEncaminhamento,
    },
    {
      enum: PlanoTabsEnum.DECLARACAO_COMPARECIMENTO,
      label: 'Declaração de comparecimento',
      testId: 'TabDeclaracaoComparecimento',
      permissions: {
        permission: Permissions.visualizarListaDeAtendimento.gerarDeclaracaoDeComparecimento,
      },
      showItem: showDeclaracaoComparecimento,
    },
    {
      enum: PlanoTabsEnum.CUIDADO_COMPARTILHADO,
      label: labelCuidadoCompartilhado,
      testId: 'TabCuidadoCompartilhado',
      permissions: {
        permission: grupoCboCuidadoCompartilhado,
        tiposEstabelecimento:
          getTiposEstabelecimentoCuidadoCompartilhado(PILOTO_VINCULACAO_CUIDADO_ENABLED) ?? undefined,
        acceptEstagio: false,
      },
      showItem: showCuidadoCompartilhado,
    },
  ]

  return {
    tabs,
    initialTab: isRetificacaoAtendimentoProcedimentos
      ? PlanoTabsEnum.ORIENTACOES
      : PlanoTabsEnum.PRESCRICAO_MEDICAMENTOS,
  }
}
